import Component from "@glimmer/component";
import { cached } from "@glimmer/tracking";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import { getOwner } from "@ember/owner";
import { cancel } from "@ember/runloop";
import { service } from "@ember/service";
import { trustHTML } from "@ember/template";
import { modifier } from "ember-modifier";
import { bind } from "discourse/lib/decorators";
import discourseLater from "discourse/lib/later";
import { emojiUnescape, emojiUrlFor } from "discourse/lib/text";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import { i18n } from "discourse-i18n";
import ChatMessageReactionsUsers from "discourse/plugins/chat/discourse/components/chat-message-reactions-users";
import { getReactionText } from "discourse/plugins/chat/discourse/lib/get-reaction-text";

let descriptionSequence = 0;

export default class ChatMessageReaction extends Component {
  @service currentUser;
  @service menu;
  @service site;

  // Hovering (desktop) or long-pressing (mobile) a reaction opens a users popup
  // centred on that reaction. Each reaction registers its own popup; the shared
  // `groupIdentifier` ensures only one is open at a time, so moving to another
  // reaction opens a fresh popup.
  registerReactionsUsersPopup = modifier((element) => {
    if (this.args.disableTooltip) {
      return;
    }

    const desktop = !this.site.mobileView;

    const instance = this.menu.register(element, {
      identifier: "chat-message-reaction-users",
      groupIdentifier: "chat-message-reaction-users",
      component: ChatMessageReactionsUsers,
      modalForMobile: true,
      arrow: true,
      offset: 15,
      placement: "bottom",
      fallbackPlacements: ["top"],
      triggers: desktop ? ["hover"] : ["hold"],
      data: {
        message: this.args.message,
        emoji: this.args.reaction.emoji,
        // Lets the popup keep itself open while the pointer is over it, so the
        // hover-to-open popup only closes once the pointer leaves both the
        // reaction and the popup.
        onContentPointerEnter: desktop
          ? this.cancelCloseReactionsUsersPopup
          : undefined,
        onContentPointerLeave: desktop
          ? this.scheduleCloseReactionsUsersPopup
          : undefined,
      },
    });
    this.#reactionsUsersPopupInstance = instance;

    if (desktop) {
      element.addEventListener(
        "pointerenter",
        this.cancelCloseReactionsUsersPopup,
        { passive: true }
      );
      element.addEventListener(
        "pointerleave",
        this.scheduleCloseReactionsUsersPopup,
        { passive: true }
      );
      element.addEventListener("focus", this.openReactionsUsersPopup, {
        passive: true,
      });
      element.addEventListener("blur", this.scheduleCloseReactionsUsersPopup, {
        passive: true,
      });
    }

    return () => {
      cancel(this.#closeReactionsUsersPopupTimer);
      element.removeEventListener(
        "pointerenter",
        this.cancelCloseReactionsUsersPopup
      );
      element.removeEventListener(
        "pointerleave",
        this.scheduleCloseReactionsUsersPopup
      );
      element.removeEventListener("focus", this.openReactionsUsersPopup);
      element.removeEventListener(
        "blur",
        this.scheduleCloseReactionsUsersPopup
      );
      instance.destroy();
      this.#reactionsUsersPopupInstance = null;
    };
  });

  descriptionId = `chat-message-reaction-description-${descriptionSequence++}`;
  #reactionsUsersPopupInstance = null;
  #closeReactionsUsersPopupTimer = null;

  get showCount() {
    return this.args.showCount ?? true;
  }

  get emojiString() {
    return `:${this.args.reaction.emoji}:`;
  }

  get emojiUrl() {
    return emojiUrlFor(this.args.reaction.emoji);
  }

  get isCountedReaction() {
    return !!(this.showCount && this.args.reaction.count);
  }

  get ariaLabel() {
    const emoji = this.args.reaction.emoji;

    if (this.isCountedReaction) {
      return i18n("chat.reactions.counted", {
        emoji,
        count: this.args.reaction.count,
      });
    }

    if (this.args.reaction.reacted) {
      return i18n("chat.reactions.remove", { emoji });
    }

    return i18n("chat.reactions.add", { emoji });
  }

  @cached
  get popoverContent() {
    if (!this.args.reaction.count || !this.args.reaction.users?.length) {
      return;
    }

    return emojiUnescape(getReactionText(this.args.reaction, this.currentUser));
  }

  get description() {
    return this.popoverContent ? trustHTML(this.popoverContent) : undefined;
  }

  // Close on a short delay so moving the pointer across the gap between the
  // reaction and the popup (or briefly off either) doesn't dismiss it.
  @bind
  scheduleCloseReactionsUsersPopup() {
    cancel(this.#closeReactionsUsersPopupTimer);
    this.#closeReactionsUsersPopupTimer = discourseLater(() => {
      this.#reactionsUsersPopupInstance?.close({ focusTrigger: false });
    }, 250);
  }

  @bind
  openReactionsUsersPopup() {
    this.cancelCloseReactionsUsersPopup();
    this.#reactionsUsersPopupInstance?.show();
  }

  @bind
  cancelCloseReactionsUsersPopup() {
    cancel(this.#closeReactionsUsersPopupTimer);
  }

  @action
  handleClick(event) {
    event.stopPropagation();

    if (!this.currentUser) {
      getOwner(this).lookup("route:application").send("showLogin");
      return;
    }

    this.args.onReaction?.(
      this.args.reaction.emoji,
      this.args.reaction.reacted ? "remove" : "add"
    );
  }

  <template>
    {{#if (and @reaction this.emojiUrl)}}
      <button
        aria-describedby={{if this.description this.descriptionId}}
        aria-label={{this.ariaLabel}}
        aria-pressed={{if
          this.isCountedReaction
          (if @reaction.reacted "true" "false")
        }}
        class={{dConcatClass
          "chat-message-reaction"
          (if @reaction.reacted "reacted")
        }}
        data-emoji-name={{@reaction.emoji}}
        {{! `interactive` is opt-out, as it is on the message itself: only an explicit
        false makes a reaction display-only. }}
        tabindex={{if (eq @interactive false) "-1" "0"}}
        title={{this.emojiString}}
        type="button"
        {{on "click" this.handleClick passive=true}}
        {{this.registerReactionsUsersPopup}}
      >
        <img
          alt={{this.emojiString}}
          class="emoji"
          height="20"
          loading="lazy"
          src={{this.emojiUrl}}
          width="20"
        />

        {{#if (and this.showCount @reaction.count)}}
          <span class="count">{{@reaction.count}}</span>
        {{/if}}
      </button>

      {{#if this.description}}
        <span
          class="sr-only"
          id={{this.descriptionId}}
        >{{this.description}}</span>
      {{/if}}
    {{/if}}
  </template>
}
